import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import Select from '../../../../components/forms/Select';
import { ClassifierListFilters, ClassifierModel, ClassifierTypeModel } from '../../../../api/admin/admin.types';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { getClassifierList } from '../../actions/classifier.actions';
import { ClassifierActionTypes } from '../../types/classifier.types';
import { getClassifierTypeList } from '../../../core/actions/classifiertypes.actions';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import { WithTranslation, withTranslation } from 'react-i18next';
import ListViewTable from '../../../../components/tables/ListViewTable';

interface ClassifierProps extends RouteComponentProps, WithTranslation {
  getClassifierList: (filters?: ClassifierListFilters, pageRequest?: PageRequest) => void;
  getClassifierTypesList: () => void;
  classifiers: ClassifierModel[] | null;
  classifierTypes: ClassifierTypeModel[];
  totalPages: number;
  totalElements: number;
}

interface ClassifierState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  filter: {
    codeFilter: {
      value: string;
    };
    nameFilter: {
      value: string;
    };
    descriptionFilter: {
      value: string;
    };
    valueFilter: {
      value: string;
    };
    classifierTypeFilter: {
      value: { label: string; value: string } | undefined;
    };
  };
}

const filterableColumns = [
  'code'
];

class Classifiers extends React.Component<ClassifierProps, ClassifierState> {
  constructor(props: ClassifierProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'ASC',
      sortedBy: 'code',
      filter: {
        codeFilter: {
          value: ''
        },
        nameFilter: {
          value: ''
        },
        descriptionFilter: {
          value: ''
        },
        valueFilter: {
          value: ''
        },
        classifierTypeFilter: {
          value: undefined
        }
      }
    };
  }

  componentDidMount() {
    this.props.getClassifierTypesList();
    this.props.getClassifierList();
  }

  onRowClick = (row: any) => (
    this.props.history.push(`/admin/classifiers/${row.original.code}/view`)
  );

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    if (!filterableColumns.includes(columnName)) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: columnName });
    return (
      this.props.getClassifierList(this.composeClassifierFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: columnName,
        sortDirection: sortDirection
      })
    );
  };

  statusCell = (props: any) => (<StatusLabel status={props}/>);

  valueCell = ({ cell, row }: { cell: any, row: any }) => (
    <div>
      {`${(cell.row.original.stringValue ?? '')} ${cell.row.original.longValue ?? ''}`}
    </div>
  );

  classifierTypeCell = (props: any) => {
    const classifierTypeObj = this.props.classifierTypes.find((d) => d.code === props);
    if (classifierTypeObj) {
      return classifierTypeObj.name;
    }
    return '';
  };

  onFilterClick = (e: FormEvent) => {
    e.preventDefault();
    this.props.getClassifierList(this.composeClassifierFilters());
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = (newValue: any, field: string) => {
    this.setState({
      ...this.state,
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    }, () => {
      this.props.getClassifierList(this.composeClassifierFilters());
    });
  };

  composeClassifierFilters = () => {
    const classifierTypeCode = this.state.filter.classifierTypeFilter.value ? this.state.filter.classifierTypeFilter.value.value : undefined;

    const filters: ClassifierListFilters = {
      name: this.state.filter.nameFilter.value,
      code: this.state.filter.codeFilter.value,
      description: this.state.filter.descriptionFilter.value,
      value: this.state.filter.valueFilter.value,
      classifierTypeCode
    };

    return filters;
  };


  getData(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      this.props.getClassifierList(this.composeClassifierFilters(), pageRequest);
      this.setState({ selectedPageNumber: pageRequest.page + 1, sortOrder: pageRequest.sortDirection, sortedBy: pageRequest.sortField });
    }
  }

  onPageNumberClick = (pageNUmber: number) => {
    const pageRequest: PageRequest = {
      page: pageNUmber - 1
    };

    this.props.getClassifierList(this.composeClassifierFilters(), pageRequest);

    this.setState({ selectedPageNumber: pageNUmber });
  };


  defineColumns() {
    return [
      { header: 'Code', accessorKey: 'code', id: 'code' },
      { header: 'Name', accessorKey: 'name', id: 'name' },
      { header: 'Description', accessorKey: 'description', id: 'description', meta: { maxWidth: 450} },
      { header: 'Value', cell: ({ cell, row }: { cell: any, row: any }) => this.valueCell({ cell, row }) },
      {
        header: 'Classifier type',
        accessorKey: 'classifierType',
        cell: (info: any) => this.classifierTypeCell(info.getValue())
      },
      {
        header: 'Status', accessorKey: 'statusType', cell: (info: any) => this.statusCell(info.getValue())
      }
    ];
  }

  render() {
    const { t } = this.props;
    return (
      <main>
        <div className="page-content">
          <div className="mt-2 mb-1 d-flex justify-content-between align-items-center">
            <h1>{t('page_title.classifiers')}</h1>
            <Link
              to={{
                pathname: '/admin/classifiers/create'
              }}
              className="btn btn-primary btn-wide"
            >
              {t('b.add_new').toUpperCase()}
            </Link>
          </div>
          <form className="container-fluid filter-container form-group form-inline d-flex flex-wrap
            justify-content-between">
            <div className="filters-container d-flex w-75 flex-wrap">
              <input
                type="text"
                value={this.state.filter.codeFilter.value}
                name="codeFilter"
                className="form-control mr-1 col edit-form-input margins pl-1 ml-1 min-width-form-input
                    filter-item-margin"
                placeholder={t('ph.filter.classifiers.by_code')}
                onChange={this.changeHandler}
              />
              <input
                type="text"
                value={this.state.filter.nameFilter.value}
                name="nameFilter"
                className="form-control mr-1 col edit-form-input pl-1 ml-0 min-width-form-input filter-item-margin"
                placeholder={t('ph.filter.classifiers.by_name')}
                onChange={this.changeHandler}
              />
              <input
                type="text"
                value={this.state.filter.descriptionFilter.value}
                name="descriptionFilter"
                className="form-control mr-1 col edit-form-input pl-1 ml-0 min-width-form-input filter-item-margin"
                placeholder={t('ph.filter.classifiers.by_description')}
                onChange={this.changeHandler}
              />
              <input
                type="text"
                value={this.state.filter.valueFilter.value}
                name="valueFilter"
                className="form-control mr-1 col edit-form-input pl-1 ml-0 min-width-form-input filter-item-margin"
                placeholder={t('ph.filter.classifiers.by_value')}
                onChange={this.changeHandler}
              />
              <Select
                className="mr-1"
                placeholder={t('ph.filter.classifiers.by_type')}
                value={this.state.filter.classifierTypeFilter.value}
                options={this.props.classifierTypes.map((d) => ({ label: d.name, value: d.code }))}
                onChange={(val) => this.dropdownChange(val, 'classifierTypeFilter')}
                equalWidthElement
                marginTop
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="classifierTypes"
              />
            </div>
            <div className="pl-1">
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}</button>
            </div>
          </form>
          {this.props.classifiers ? (
            <div className="table-list">
              <ListViewTable
                data={this.props.classifiers}
                columns={this.defineColumns()}
                getTrProps={this.onRowClick}
                sortBy={this.sortBy}
                sortOrder={this.state.sortOrder}
                sortedBy={this.state.sortedBy}
                paginationEnabled={true}
                totalPages={this.props.totalPages}
                totalElements={this.props.totalElements}
                selectedPageNumber={this.state.selectedPageNumber}
                getDataFn={(pageRequest: PageRequest) => this.getData(pageRequest)}
              />
            </div>
          ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  classifiers: state.admin.classifiers.classifiers,
  classifierTypes: state.core.classifierTypes.classifierTypes,
  totalPages: state.admin.classifiers.totalPages,
  totalElements: state.admin.classifiers.totalElements
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, ClassifierActionTypes>) => ({
  getClassifierList: (filters?: ClassifierListFilters, pageRequest?: PageRequest) => dispatch(getClassifierList(filters, pageRequest)),
  getClassifierTypesList: () => dispatch(getClassifierTypeList())
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(Classifiers));
